/**
 * B46 — ownership rules for USER-STAMPED TRANSIENT SURFACE STATE.
 *
 * Sibling of the B26/F-113 wizard-blob rules in `hooks/onboardingReducer.ts`
 * (`loadPersistedStateForUser` / `purgeForeignPersistedState`). Same family,
 * same one-sentence guarantee — **one account's state can never reach another
 * account** — but a DIFFERENT default for the unstamped case, and the
 * difference is deliberate. See "Unstamped" below before touching either side.
 *
 * ── What leaked (founder report, 2026-08-30) ───────────────────────────────
 * A brand-new account, freshly signed up on a device whose previous session
 * belonged to a DIFFERENT account, saw "Week 9 is in." — the draft-approved ✓
 * chip — during onboarding and in the app header. Week 9 was the PRIOR user's
 * week. Nothing about the new account had a week 9, or a plan at all.
 *
 * ── Where that state lives ─────────────────────────────────────────────────
 * NOT localStorage, and NOT a react-query cache. The chip travels as react-
 * router `location.state` (`routes/plan-draft.tsx` navigates to the origin
 * surface with `{ state: { draftChip } }`). React-router keeps `location.state`
 * under `window.history.state.usr`, which is part of the SESSION HISTORY ENTRY
 * — the browser (and the shell's WKWebView) persists and restores it across
 * reloads, warm relaunches and session restores, and the router re-reads it
 * when it rebuilds the initial location. Sign-out does not clear it: the SPA
 * never unmounts across a sign-out → sign-up, so both the history entry AND
 * the mounted chip component carry straight into the next account's session.
 *
 * ── The rule ───────────────────────────────────────────────────────────────
 * Stamp the chip with its author at MINT time; the surface renders it only for
 * that author.
 *
 *   - stamped with THIS viewer   → render (the ordinary case).
 *   - stamped with ANOTHER id    → FOREIGN. Never rendered, and scrubbed out of
 *                                  the history entry on the next identity
 *                                  change (`scrubStampedHistoryState`).
 *   - no viewer signed in        → nothing rendered. A stamped confirmation has
 *                                  no audience when nobody is signed in.
 *   - UNSTAMPED                  → NOT rendered. See below.
 *
 * ── Unstamped: why this differs from B26, and why B26 is still right ───────
 * B26's rule for the onboarding wizard blob is "unclaimed is not foreign":
 * an unstamped blob is answers TYPED AT THIS BROWSER BY A PERSON WHO WAS NOT
 * YET SIGNED IN, and the only person who can be sitting there signing in is
 * the person who typed them — so the first account to sign in claims them.
 * That rescue exists because the wizard was walkable signed out; discarding
 * those answers stranded the first organic external user.
 *
 * That case CANNOT arise for the chip. The chip is minted on exactly one code
 * path (`plan-draft.tsx` `handleApprove`), behind a route that renders
 * `<Navigate to="/landing">` without `auth.user`. Every chip is minted by a
 * signed-in account, so "unstamped" can never mean "pre-auth work worth
 * rescuing" — it can only mean a chip left by a build that predates this
 * stamp, i.e. unattributable, i.e. exactly the B46 leak. There is also nothing
 * to rescue: the chip is a five-second ✓ confirmation of a save that already
 * completed on the server.
 *
 * So: the wizard blob KEEPS unclaimed state (B26 untouched, and fenced by test
 * in `stampedSurfaceState.test.ts` alongside these rules, so neither rule can
 * be "fixed" by breaking the other). Stamped surface state DROPS it.
 *
 * Pure functions plus one narrow history write — no React, unit-testable.
 */

/** react-router keeps `location.state` under this key in `window.history.state`. */
const ROUTER_STATE_KEY = 'usr';

/** The chip copy itself ("Week 9 is in."). */
export const DRAFT_CHIP_KEY = 'draftChip';

/** The chip's CLAIM — the auth user id that minted it (see file header). */
export const DRAFT_CHIP_OWNER_KEY = 'draftChipUserId';

/** The shape `plan-draft.tsx` hands to `navigate(..., { state })`. */
export type StampedDraftChipState = {
  [DRAFT_CHIP_KEY]: string;
  [DRAFT_CHIP_OWNER_KEY]: string | null;
};

/**
 * Mint the router state for a landed-draft chip, stamped with its author.
 *
 * `userId` is nullable only so the caller never has to prove non-null at the
 * call site; a null stamp produces state that `readStampedChipForUser` will
 * refuse to render for anyone (fail-closed — an unattributable chip is exactly
 * what B46 was).
 */
export function stampDraftChip(
  text: string,
  userId: string | null | undefined,
): StampedDraftChipState {
  return {
    [DRAFT_CHIP_KEY]: text,
    [DRAFT_CHIP_OWNER_KEY]: typeof userId === 'string' && userId ? userId : null,
  };
}

/**
 * Read the landed-draft chip out of a react-router `location.state` FOR a
 * given viewer, applying the ownership rule in the file header. Returns the
 * chip copy when this viewer minted it, and null in every other case
 * (no viewer, no chip, malformed chip, foreign stamp, or no stamp at all).
 *
 * Ownership-aware by construction: there is no ownership-agnostic reader for
 * this state, deliberately — B46 was a surface reading it without asking whose
 * it was, and a second reader would just reopen that door.
 */
export function readStampedChipForUser(
  state: unknown,
  viewerId: string | null | undefined,
): string | null {
  if (!viewerId) return null;
  if (typeof state !== 'object' || state === null) return null;

  const bag = state as Record<string, unknown>;
  const text = bag[DRAFT_CHIP_KEY];
  if (typeof text !== 'string' || text === '') return null;

  const owner = bag[DRAFT_CHIP_OWNER_KEY];
  // Unstamped (legacy blob from a pre-B46 build) → unattributable → dropped.
  if (typeof owner !== 'string' || owner === '') return null;
  // Stamped with someone ELSE → foreign → absent.
  if (owner !== viewerId) return null;

  return text;
}

/**
 * Scrub router-carried state out of the CURRENT history entry, so a reload,
 * a warm relaunch, or a session restore cannot resurrect it under whoever is
 * signed in by then.
 *
 * Deliberately blunt: it drops the whole `usr` bag rather than picking the
 * chip keys out of it. Router state is per-navigation and transient by
 * definition — nothing durable is supposed to live there — and a surgical
 * delete would need updating every time another surface starts carrying
 * state. It leaves react-router's own `key` / `idx` bookkeeping intact.
 *
 * Best-effort: swallows history failures the way `persistState` swallows
 * storage failures. A blocked write just means the ownership check in
 * `readStampedChipForUser` is the only line of defence — which is the line
 * that actually decides what renders.
 *
 * B255/B256 (security F2) — this same blunt wipe is also what closes the
 * coach-card leak: `CoachStories.tsx` mints `{ coachCard: { ..., ownerId } }`
 * and `routes/chat.tsx`'s roster carries `{ coachList: true }`, both under
 * the SAME `usr` bag this function already drops whole, so no key-specific
 * change was needed here. `readCoachCardEntry` (`chatContextMode.ts`) is the
 * ownership check, this file's `readStampedChipForUser` pattern; this scrub
 * is the second layer, for whichever entry happens to be the CURRENT one
 * when identity changes.
 */
export function scrubStampedHistoryState(): void {
  if (typeof window === 'undefined') return;
  try {
    const current = window.history.state as Record<string, unknown> | null;
    if (!current || current[ROUTER_STATE_KEY] == null) return;
    window.history.replaceState({ ...current, [ROUTER_STATE_KEY]: null }, '');
  } catch {
    /* history unavailable — see doc comment. */
  }
}
